/**
 * Utilidades de animación sin dependencias (CSS + IntersectionObserver + requestAnimationFrame).
 * Todas respetan prefers-reduced-motion.
 */
import { useEffect, useRef, useState } from 'react';

export function prefersReducedMotion() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function useReducedMotion() {
  const [reduced, setReduced] = useState(prefersReducedMotion);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const on = () => setReduced(mq.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return reduced;
}

/** Añade la clase `is-inview` una sola vez cuando el elemento entra en pantalla. */
export function useInView({ rootMargin = '0px 0px -10% 0px', once = true } = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === 'undefined' || prefersReducedMotion()) {
      setInView(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            if (once) io.disconnect();
          } else if (!once) {
            setInView(false);
          }
        });
      },
      { rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [rootMargin, once]);
  return [ref, inView];
}

/** Progreso 0→1 de un elemento mientras cruza la pantalla (para animaciones ligadas al scroll). */
export function useScrollProgress(ref, { start = 1, end = 0 } = {}) {
  const [p, setP] = useState(0);
  useEffect(() => {
    if (prefersReducedMotion()) {
      setP(1);
      return undefined;
    }
    let raf = 0;
    const calc = () => {
      raf = 0;
      const el = ref.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      // start: borde superior del elemento en (start * vh); end: borde inferior en (end * vh)
      const from = start * vh;
      const to = end * vh;
      const total = r.height + (from - to);
      const done = from - r.top;
      setP(Math.min(1, Math.max(0, done / (total || 1))));
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(calc);
    };
    calc();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [ref, start, end]);
  return p;
}

/** Valor numérico que «cuenta» hasta el objetivo (precio animado). */
export function useCountUp(target, { duration = 450 } = {}) {
  const [value, setValue] = useState(target);
  const fromRef = useRef(target);
  useEffect(() => {
    const from = fromRef.current;
    if (from === target || prefersReducedMotion()) {
      fromRef.current = target;
      setValue(target);
      return undefined;
    }
    let raf = 0;
    const t0 = performance.now();
    const tick = (now) => {
      const k = Math.min(1, (now - t0) / duration);
      const eased = 1 - Math.pow(1 - k, 3);
      const v = from + (target - from) * eased;
      setValue(v);
      if (k < 1) raf = requestAnimationFrame(tick);
      else fromRef.current = target;
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      fromRef.current = target;
    };
  }, [target, duration]);
  return value;
}

/**
 * Vuela una miniatura desde `fromEl` hasta `toEl` en arco (Web Animations API).
 * Devuelve una promesa que se resuelve al terminar. Con reduced-motion no hace nada.
 */
export function flyToCart(fromEl, toEl, { size = 64 } = {}) {
  return new Promise((resolve) => {
    if (!fromEl || !toEl || prefersReducedMotion() || !document.body.animate) {
      resolve();
      return;
    }
    const a = fromEl.getBoundingClientRect();
    const b = toEl.getBoundingClientRect();
    const ghost = fromEl.cloneNode(true);
    ghost.setAttribute('aria-hidden', 'true');
    Object.assign(ghost.style, {
      position: 'fixed',
      left: `${a.left + a.width / 2 - size / 2}px`,
      top: `${a.top + a.height / 2 - size / 2}px`,
      width: `${size}px`,
      height: `${size}px`,
      zIndex: 80,
      pointerEvents: 'none',
      borderRadius: '999px',
      overflow: 'hidden',
      margin: 0,
    });
    document.body.appendChild(ghost);
    const dx = b.left + b.width / 2 - (a.left + a.width / 2);
    const dy = b.top + b.height / 2 - (a.top + a.height / 2);
    const lift = Math.min(-80, dy * 0.3 - 120);
    const anim = ghost.animate(
      [
        { transform: 'translate(0,0) scale(1) rotate(0deg)', opacity: 1 },
        { transform: `translate(${dx * 0.5}px, ${lift}px) scale(.8) rotate(180deg)`, opacity: 1, offset: 0.45 },
        { transform: `translate(${dx}px, ${dy}px) scale(.25) rotate(360deg)`, opacity: 0.6 },
      ],
      { duration: 600, easing: 'cubic-bezier(0.45, 0, 0.2, 1)' },
    );
    anim.onfinish = () => {
      ghost.remove();
      resolve();
    };
  });
}

/** FLIP: anima `el` como si partiera de `fromRect` (morph tarjeta → hoja). */
export function morphFrom(el, fromRect, { duration = 380 } = {}) {
  if (!el || !fromRect || prefersReducedMotion() || !el.animate) return;
  const to = el.getBoundingClientRect();
  if (!to.width || !to.height) return;
  const sx = fromRect.width / to.width;
  const sy = fromRect.height / to.height;
  const dx = fromRect.left - to.left;
  const dy = fromRect.top - to.top;
  el.animate(
    [
      { transformOrigin: 'top left', transform: `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`, borderRadius: '22px' },
      { transformOrigin: 'top left', transform: 'none' },
    ],
    { duration, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
  );
}

/** Reinicia una animación CSS de una clase (rebote del contador, etc.). */
export function replayClass(el, cls) {
  if (!el || prefersReducedMotion()) return;
  el.classList.remove(cls);
  // eslint-disable-next-line no-unused-expressions
  el.offsetWidth;
  el.classList.add(cls);
}
